// Pancha Healing — Sacred Report (15 sections).
// Used by both the admin OrderDetail page and the public customer dashboard.
// All visual helpers are co-located here so the component is self-contained.
import DOMPurify from "dompurify";
import {
  getCrystalGradient,
  ELEMENT_PALETTE,
  LP_ARCHETYPE, NN_ARCHETYPE, ZODIAC_ARCHETYPE, PY_THEME,
  zodiacCelestialPhrase, CHAKRA_MANTRAS, CHAKRA_YANTRAS,
  SECTION_SUBTITLES, RX_BADGE_COLORS, badgeKeyFromSource,
  BRAND_LOTUS_SVG, FOOTER_EMBLEM_SVG, AFFIRMATION_LOTUS_SVG,
  ORB_SHADOW,
} from "../lib/sacredReportAssets";
import { buildBraceletLayout, INTENTIONS } from "../lib/crystalPrescription";
import { crystalColor } from "../lib/crystalColors";
import { ELEMENT_GLYPH, ELEMENTS, whyDominant } from "../lib/panchaEngine";
import { renderReport } from "../lib/sacredReports";
import { CHAKRA } from "../lib/chakra";
import { calcPersonalYear, getCurrentYear, getNextYear } from "../lib/numerology";
import { WRIST_SIZES } from "../lib/constants";

const SVG_SANITIZER = { USE_PROFILES: { svg: true, svgFilters: true } };
// SAFETY: every call site below feeds `safeSvg` a HARDCODED SVG constant from
// sacredReportAssets.js (BRAND_LOTUS_SVG / AFFIRMATION_LOTUS_SVG / FOOTER_EMBLEM_SVG /
// CHAKRA_YANTRAS dict). No user input ever flows into these strings; the
// DOMPurify pass is defence-in-depth so any future asset edits cannot introduce
// XSS through this code path.
const safeSvg = (s) => DOMPurify.sanitize(s || "", SVG_SANITIZER);

export default function SacredReport({ order }) {
  if (!order) return null;
  const { numerology, panchaBeads, prescriptionCrystals, dominantElement, fullName } = order;
  const layout = buildBraceletLayout(panchaBeads, prescriptionCrystals);
  const curYr = getCurrentYear();
  const nxtYr = getNextYear();
  const pyCur = order.dob ? calcPersonalYear(order.dob, curYr) : numerology.personalYear;
  const pyNxt = order.dob ? calcPersonalYear(order.dob, nxtYr) : numerology.personalYearNext;
  const report = renderReport(numerology.lifePath, fullName, {
    currentYear: curYr,
    nextYear: nxtYr,
    nameNumber: numerology.nameNumber,
  });
  const chakra = CHAKRA[dominantElement];
  const wristDef = WRIST_SIZES.find((w) => w.value === order.wristSize);

  if (!report) return null;

  return (
    <section
      className="mt-2 p-6 md:p-10 print-card rounded-2xl"
      data-testid="sacred-report"
      style={{ background: "linear-gradient(135deg, #FAF8F4 0%, #F5F0E8 100%)" }}
    >
      {/* Pancha Element Foundation */}
      <PanchaFoundation panchaBeads={panchaBeads} dominantElement={dominantElement} />

      {/* Prescription Crystals */}
      <PrescriptionList prescriptionCrystals={prescriptionCrystals} />

      {/* Bead Arrangement */}
      <BeadArrangement layout={layout} panchaBeads={panchaBeads} wristDef={wristDef} />

      {/* ✦ Brand header */}
      <div className="text-center sacred-report-header mt-12" style={{ paddingBottom: "14px" }}>
        <span
          style={{ display: "inline-block", marginBottom: "10px" }}
          dangerouslySetInnerHTML={{ __html: safeSvg(BRAND_LOTUS_SVG) }}
        />
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", color: "#2C0A5E", letterSpacing: "0.04em", fontWeight: 500, marginBottom: "4px" }}>
          Pancha Healing
        </div>
        <div style={{ fontSize: "9px", letterSpacing: "0.32em", color: "#6A0DAD", fontFamily: "Jost, sans-serif", fontWeight: 500 }}>
          SACRED PRESCRIPTION
        </div>
      </div>
      <div style={{ height: "1px", background: "linear-gradient(to right, transparent, #D4A855, transparent)", margin: "0 auto 24px" }} />

      {/* Customer name editorial */}
      <div className="text-center" style={{ padding: "6px 0 22px", borderBottom: "1px solid rgba(212,168,85,0.3)", marginBottom: "26px" }}>
        <p style={{ margin: "0 0 4px", fontSize: "11px", letterSpacing: "0.3em", color: "#6B5E7E", fontFamily: "Jost, sans-serif" }}>
          A SACRED BLUEPRINT FOR
        </p>
        <h1 style={{ margin: "0 0 6px", fontSize: "38px", fontWeight: 400, color: "#2C0A5E", fontStyle: "italic", fontFamily: "'Cormorant Garamond', serif", lineHeight: 1.05 }}>
          {fullName}
        </h1>
        <p style={{ margin: 0, fontSize: "12px", color: "#6B5E7E", fontFamily: "Jost, sans-serif" }}>
          {formatDobLong(order.dob)} · Born under the {zodiacCelestialPhrase(numerology.zodiac)}
        </p>
      </div>

      {/* Sacred Blueprint Grid */}
      <SacredDivider />
      <SacredSectionTitle title="SACRED BLUEPRINT" subtitle={SECTION_SUBTITLES["SACRED BLUEPRINT"]} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-1" data-testid="sacred-blueprint-grid">
        <BPCell label="LIFE PATH" number={numerology.lifePath} subtitle={LP_ARCHETYPE[numerology.lifePath]} />
        <BPCell label="NAME NUMBER" number={numerology.nameNumber} subtitle={NN_ARCHETYPE[numerology.nameNumber]} />
        <BPCell label="ZODIAC" numberText={numerology.zodiac} subtitle={ZODIAC_ARCHETYPE[numerology.zodiac]} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_2fr_1.5fr] gap-2.5 mt-2.5">
        <BPHighlightCell kind="now" label={`PERSONAL YEAR ${curYr}`} number={pyCur} subtitle={PY_THEME[pyCur]} />
        <BPHighlightCell kind="next" label={`PERSONAL YEAR ${nxtYr}`} number={pyNxt} subtitle={PY_THEME[pyNxt]} />
        <BPCell label="RULING PLANET" numberText={numerology.rulingPlanet} subtitle="Cosmic guide" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mt-2.5">
        <BPCell label="LUCKY COLOUR" text={numerology.luckyColor} />
        <BPCell label="LUCKY DAY" text={numerology.luckyDay} />
        <BPCell label="LUCKY NUMBERS" text={numerology.luckyNumbers.join(", ")} />
      </div>

      {/* Dominant Chakra */}
      {chakra && <DominantChakraCard chakra={chakra} dominantElement={dominantElement} />}

      {/* Soul Essence */}
      <SacredDivider />
      <SacredSectionTitle title="SOUL ESSENCE" subtitle={SECTION_SUBTITLES["SOUL ESSENCE"]} big />
      <SoulEssenceCard firstName={(fullName || "").split(" ")[0]} body={report.soul} />

      {/* Long-form sections */}
      <SacredDivider />
      <SacredSectionTitle title="LIFE PATH MISSION" subtitle={SECTION_SUBTITLES["LIFE PATH MISSION"]} />
      <ContentCard body={report.lp} />

      <SacredDivider />
      <SacredSectionTitle title="NAME NUMBER ENERGY" subtitle={SECTION_SUBTITLES["NAME NUMBER ENERGY"]} />
      <ContentCard body={report.nn} />

      <SacredDivider />
      <SacredSectionTitle title="CORE CHARACTERISTICS" subtitle={SECTION_SUBTITLES["CORE CHARACTERISTICS"]} />
      <ContentCard body={report.ch} />

      <SacredDivider />
      <SacredSectionTitle title={`PERSONAL YEAR · ${curYr}`} subtitle={SECTION_SUBTITLES["PERSONAL YEAR"]} />
      <ContentCard body={report.cur} />

      <SacredDivider />
      <SacredSectionTitle title={`ENERGY AHEAD · ${nxtYr}`} subtitle={SECTION_SUBTITLES["ENERGY AHEAD"]} />
      <ContentCard body={report.fut} />

      <SacredDivider />
      <SacredSectionTitle title="KARMA LESSON" subtitle={SECTION_SUBTITLES["KARMA LESSON"]} />
      <ContentCard body={report.kar} />

      <SacredDivider />
      <SacredSectionTitle title="SACRED GIFTS" subtitle={SECTION_SUBTITLES["SACRED GIFTS"]} />
      <ContentCard body={report.gifts} />

      <SacredDivider />
      <SacredSectionTitle title="SPIRITUAL PRACTICES" subtitle={SECTION_SUBTITLES["SPIRITUAL PRACTICES"]} />
      <ContentCard body={report.prac} />

      {/* Sacred Guidance */}
      <SacredDivider />
      <SacredSectionTitle title="SACRED GUIDANCE" subtitle={SECTION_SUBTITLES["SACRED GUIDANCE"]} />
      <div className="grid sm:grid-cols-2 gap-3.5">
        <GuidanceCard variant="do" items={report.dos} />
        <GuidanceCard variant="avoid" items={report.dont} />
      </div>

      {/* Personal Affirmation */}
      <SacredDivider big />
      <SacredSectionTitle title="YOUR SACRED AFFIRMATION" subtitle={SECTION_SUBTITLES["SACRED AFFIRMATION"]} big />
      <div
        className="rounded-2xl text-center relative affirmation-card"
        data-testid="affirmation-card"
        style={{
          background: "linear-gradient(135deg, #2C0A5E 0%, #6A0DAD 100%)",
          padding: "36px 28px 32px",
          boxShadow: "0 12px 36px rgba(106,13,173,0.25)",
          WebkitPrintColorAdjust: "exact",
          printColorAdjust: "exact",
        }}
      >
        <div aria-hidden style={{ position: "absolute", inset: "-1px", borderRadius: "16px", border: "1px solid rgba(212,168,85,0.6)", pointerEvents: "none" }} />
        <div style={{ fontSize: "14px", color: "#D4A855", letterSpacing: "0.4em", marginBottom: "16px", fontFamily: "Jost, sans-serif" }}>✦ ✦ ✦</div>
        <span style={{ display: "inline-block", marginBottom: "10px" }} dangerouslySetInnerHTML={{ __html: safeSvg(AFFIRMATION_LOTUS_SVG) }} />
        <p style={{ margin: "0 0 14px", fontSize: "20px", lineHeight: 1.7, color: "white", fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic" }}>
          &ldquo;{report.aff}&rdquo;
        </p>
        <div style={{ fontSize: "14px", color: "#D4A855", letterSpacing: "0.4em", marginTop: "14px", fontFamily: "Jost, sans-serif" }}>✦ ✦ ✦</div>
      </div>

      {/* Footer */}
      <div className="text-center" style={{ marginTop: "32px", paddingTop: "20px", borderTop: "1px solid rgba(212,168,85,0.3)" }}>
        <span style={{ display: "inline-block" }} dangerouslySetInnerHTML={{ __html: safeSvg(FOOTER_EMBLEM_SVG) }} />
        <div style={{ fontSize: "18px", color: "#2C0A5E", fontStyle: "italic", margin: "8px 0 4px", fontFamily: "'Cormorant Garamond', serif" }}>
          Pancha Healing
        </div>
        <div style={{ fontSize: "10px", color: "#6B5E7E", letterSpacing: "0.25em", fontFamily: "Jost, sans-serif" }}>
          YOUR CRYSTALS · PRESCRIBED
        </div>
      </div>
    </section>
  );
}

// ─────────── Pre-report sections (Element / Prescription / Bead Arrangement) ───────────

function PanchaFoundation({ panchaBeads, dominantElement }) {
  return (
    <section className="mb-10">
      <div className="text-center">
        <div className="ph-overline" style={{ color: "#D4A855" }}>Pancha Element Foundation</div>
        <p className="font-serif italic text-[#6B5E7E] mt-1">
          Earth, Water, Fire, Air and Ether — one 8mm stone for each. Your whole bracelet is built around your dominant element.
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
        {panchaBeads.map((b) => {
          const el = ELEMENTS[b.element];
          const isDominant = b.element === dominantElement;
          return (
            <div
              key={b.element}
              className="text-center rounded-xl p-4"
              style={{
                background: isDominant ? ELEMENT_PALETTE[b.element]?.bgStrong || "white" : "white",
                border: `1px solid ${isDominant ? "#D4A855" : "rgba(106,13,173,0.18)"}`,
                boxShadow: isDominant ? "0 6px 20px rgba(212,168,85,0.2)" : "0 2px 8px rgba(106,13,173,0.05)",
              }}
            >
              <div className="text-2xl mb-1" aria-hidden>{ELEMENT_GLYPH[b.element]}</div>
              <div className="text-[10px] tracking-[0.2em] text-[#6B5E7E]" style={{ fontFamily: "Jost, sans-serif" }}>{el?.sanskrit?.toUpperCase()}</div>
              <div className="font-serif text-[#2C0A5E] text-base">{el?.name}</div>
              <div className="text-xs text-[#6A0DAD] mt-1.5 italic">{b.crystal}</div>
              {isDominant && (
                <div className="text-[9px] mt-2 tracking-[0.2em]" style={{ color: "#8B6914", fontFamily: "Jost, sans-serif" }}>
                  ★ DOMINANT
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-center text-[#6B5E7E] mt-4 italic">{whyDominant(dominantElement)}</p>
    </section>
  );
}

function PrescriptionList({ prescriptionCrystals }) {
  return (
    <section className="mb-10">
      <div className="text-center">
        <div className="ph-overline" style={{ color: "#D4A855" }}>Prescription Crystals</div>
        <p className="font-serif italic text-[#6B5E7E] mt-1">
          The stones picked specifically for you — your birth number, your name, your zodiac, and what you asked for.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        {prescriptionCrystals.map((c, i) => {
          const grad = getCrystalGradient(c.crystal);
          const badgeKey = badgeKeyFromSource(c.source);
          const badge = RX_BADGE_COLORS[badgeKey];
          return (
            <div
              key={`${c.crystal}-${i}`}
              className="rounded-xl p-4 flex gap-3"
              style={{
                background: "white",
                border: "1px solid rgba(106,13,173,0.15)",
                boxShadow: "0 2px 10px rgba(106,13,173,0.04)",
              }}
            >
              <div
                className="shrink-0"
                style={{
                  width: 48, height: 48, borderRadius: "50%",
                  background: grad || crystalColor(c.crystal) || "#E8DBFF",
                  boxShadow: ORB_SHADOW,
                }}
                aria-hidden
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-[#2C0A5E]">{c.crystal}</div>
                  <span
                    className="text-[9px] px-2 py-0.5 rounded-full tracking-[0.16em]"
                    style={{ background: badge?.bg, color: badge?.text, fontFamily: "Jost, sans-serif" }}
                  >
                    {(c.source || "").toUpperCase()}
                  </span>
                  {c.beadCount > 1 && (
                    <span className="text-xs text-[#6B5E7E]">× {c.beadCount}</span>
                  )}
                </div>
                <p className="text-xs text-[#6B5E7E] mt-1 italic">{c.reason}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BeadArrangement({ layout, panchaBeads, wristDef }) {
  return (
    <section className="mb-2">
      <div className="text-center">
        <div className="ph-overline" style={{ color: "#D4A855" }}>Your Bracelet Arrangement</div>
        <p className="font-serif italic text-[#6B5E7E] mt-1">
          {layout.length} beads · {wristDef?.label || ""}
        </p>
      </div>
      <div className="mt-5 rounded-xl p-5" style={{ background: "white", border: "1px solid rgba(106,13,173,0.12)" }}>
        <div className="flex flex-wrap justify-center gap-1.5">
          {layout.map((b, i) => (
            <div
              key={`${b.crystal}-${i}`}
              title={`${b.crystal} (8mm${b.isPancha ? " · Pancha" : ""})`}
              style={{
                width: b.isPancha ? 28 : 22,
                height: b.isPancha ? 28 : 22,
                borderRadius: "50%",
                background: getCrystalGradient(b.crystal) || crystalColor(b.crystal) || "#E8DBFF",
                boxShadow: ORB_SHADOW,
                border: b.isPancha ? "1.5px solid #D4A855" : "none",
              }}
              aria-hidden
            />
          ))}
        </div>
        <ul className="mt-4 text-xs text-[#6B5E7E] space-y-1" style={{ fontFamily: "Georgia, serif" }}>
          {panchaBeads.map((b, i) => (
            <li key={b.element}>
              Group {i + 1}: {ELEMENTS[b.element].sanskrit} 8mm ({b.crystal}) + prescription beads
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function DominantChakraCard({ chakra, dominantElement }) {
  return (
    <>
      <SacredDivider />
      <SacredSectionTitle title="DOMINANT CHAKRA" subtitle={SECTION_SUBTITLES["DOMINANT CHAKRA"]} />
      <div
        className="rounded-2xl text-center"
        style={{
          background: "linear-gradient(135deg, #FFE5E5 0%, #FFEDD5 16%, #FEF9C3 33%, #DCFCE7 50%, #DBEAFE 66%, #E0E7FF 83%, #F3E8FF 100%)",
          padding: "28px 24px",
          border: "1px solid rgba(212,168,85,0.5)",
          boxShadow: "0 4px 24px rgba(106,13,173,0.08)",
        }}
        data-testid="chakra-card"
      >
        <div
          style={{ display: "inline-block", marginBottom: "10px" }}
          dangerouslySetInnerHTML={{ __html: safeSvg(CHAKRA_YANTRAS[chakra.name]) }}
          data-testid={`chakra-yantra-${chakra.name}`}
        />
        <div style={{ fontSize: "30px", color: "#2C0A5E", fontWeight: 500, letterSpacing: "0.04em", marginBottom: "4px", fontFamily: "'Cormorant Garamond', serif" }}>
          {chakra.name}
        </div>
        <div style={{ fontSize: "12px", color: "#8B47D9", letterSpacing: "0.18em", fontStyle: "italic", fontFamily: "Jost, sans-serif", marginBottom: "18px" }}>
          {chakra.english.toUpperCase()} · {ELEMENTS[dominantElement].name.toUpperCase()} · {(CHAKRA_MANTRAS[chakra.name]?.petals || "")} PETALS
        </div>
        {(chakra.intro || chakra.petalNote) && (
          <p style={{ margin: "0 auto 18px", maxWidth: "520px", fontSize: "13px", lineHeight: 1.65, color: "#1A1030", fontFamily: "Georgia, serif", padding: "0 8px" }}>
            {chakra.intro}
            {chakra.petalNote ? ` ${chakra.petalNote}` : ""}
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left" style={{ background: "rgba(255,255,255,0.7)", borderRadius: "12px", padding: "16px 20px", marginBottom: "18px" }}>
          <ChakraCellV2 label="ELEMENT" value={`${ELEMENTS[dominantElement].name} (${ELEMENTS[dominantElement].sanskrit})`} />
          <ChakraCellV2 label="SACRED COLOR" value={chakra.color} />
          <ChakraCellV2 label="LOCATION" value={chakra.location} />
          <ChakraCellV2 label="GOVERNS" value={chakra.governs} />
        </div>
        <div style={{ marginTop: "8px" }}>
          <div style={{ fontSize: "10px", letterSpacing: "0.25em", color: "#6A0DAD", fontFamily: "Jost, sans-serif", marginBottom: "6px" }}>
            YOUR BIJA MANTRA
          </div>
          <div className="flex items-center justify-center gap-4">
            <div style={{ fontSize: "54px", color: "#2C0A5E", fontWeight: 500, letterSpacing: "0.06em", lineHeight: 1, fontFamily: "'Cormorant Garamond', serif" }} data-testid="bija-roman">
              {CHAKRA_MANTRAS[chakra.name]?.roman || chakra.mantra}
            </div>
            <div style={{ width: "1px", height: "44px", background: "rgba(106,13,173,0.3)" }} />
            <div style={{ fontSize: "54px", color: "#2C0A5E", lineHeight: 1, fontFamily: "'Tiro Devanagari Sanskrit', serif" }} data-testid="bija-devanagari">
              {CHAKRA_MANTRAS[chakra.name]?.devanagari || ""}
            </div>
          </div>
          <div style={{ fontSize: "13px", color: "#1A1030", fontFamily: "Georgia, serif", marginTop: "12px", maxWidth: "480px", marginLeft: "auto", marginRight: "auto", lineHeight: 1.65 }}>
            {chakra.bijaNote || `${chakra.mantra} is the sound that switches on your ${chakra.english}.`} Try humming or chanting it 108 times a day (108 is a sacred number in yoga — it only takes a few minutes) — anywhere, anytime.
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────── Visual helpers ───────────

function formatDobLong(dob) {
  if (!dob) return "";
  const [y, m, d] = dob.split("-").map((x) => parseInt(x, 10));
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  if (!y || !m || !d) return dob;
  return `${d} ${months[m - 1]} ${y}`;
}

function SacredDivider({ big = false }) {
  return (
    <div className="text-center" style={{ margin: big ? "36px 0 6px" : "30px 0 6px" }}>
      <span style={{ color: "#D4A855", fontSize: big ? "22px" : "18px", letterSpacing: big ? "0.3em" : "0.2em" }}>
        {big ? "❋ ✦ ❋ ✦ ❋" : "❋ ✦ ❋"}
      </span>
    </div>
  );
}

function SacredSectionTitle({ title, subtitle, big = false }) {
  return (
    <>
      <h2 className="text-center section-title" style={{ margin: big ? "10px 0 8px" : "8px 0 6px", fontSize: big ? "15px" : "13px", letterSpacing: big ? "0.32em" : "0.28em", color: "#6A0DAD", fontFamily: "Jost, sans-serif", fontWeight: 500 }}>
        {title}
      </h2>
      {subtitle && (
        <p className="text-center section-subtitle" style={{ margin: "0 0 18px", fontSize: big ? "13px" : "12px", color: "#6B5E7E", fontFamily: "Georgia, serif", fontStyle: "italic" }}>
          {subtitle}
        </p>
      )}
    </>
  );
}

function BPCell({ label, number, numberText, text, subtitle }) {
  return (
    <div className="text-center" style={{ background: "white", border: "1px solid rgba(106,13,173,0.15)", borderRadius: "12px", padding: "14px 12px" }}>
      <div style={{ fontSize: "9px", letterSpacing: "0.2em", color: "#6B5E7E", fontFamily: "Jost, sans-serif", marginBottom: "6px", textTransform: "uppercase" }}>
        {label}
      </div>
      {number != null && (
        <div style={{ fontSize: "36px", color: "#6A0DAD", fontWeight: 400, lineHeight: 1, fontFamily: "'Cormorant Garamond', serif" }}>{number}</div>
      )}
      {numberText && (
        <div style={{ fontSize: "22px", color: "#6A0DAD", fontWeight: 400, paddingTop: "4px", fontFamily: "'Cormorant Garamond', serif" }}>{numberText}</div>
      )}
      {text && (
        <div style={{ fontSize: "13px", color: "#2C0A5E", fontStyle: "italic", fontFamily: "Georgia, serif", paddingTop: "4px" }}>{text}</div>
      )}
      {subtitle && (
        <div style={{ fontSize: "10px", color: "#6B5E7E", marginTop: "4px", fontStyle: "italic", fontFamily: "Georgia, serif" }}>{subtitle}</div>
      )}
    </div>
  );
}

function BPHighlightCell({ kind, label, number, subtitle }) {
  const isNow = kind === "now";
  const bg = isNow ? "linear-gradient(135deg, #FFF8E1 0%, #FFE9B0 100%)" : "linear-gradient(135deg, #F5F0FF 0%, #E8DBFF 100%)";
  const border = isNow ? "#D4A855" : "rgba(106,13,173,0.4)";
  const text = isNow ? "#8B6914" : "#6A0DAD";
  const badge = isNow ? "#D4A855" : "#6A0DAD";
  return (
    <div className="text-center relative" data-testid={`py-${kind}`} style={{ background: bg, border: `1px solid ${border}`, borderRadius: "12px", padding: "14px" }}>
      <div style={{ position: "absolute", top: "6px", right: "8px", background: badge, color: "white", fontSize: "8px", padding: "2px 6px", borderRadius: "8px", letterSpacing: "0.1em", fontFamily: "Jost, sans-serif" }}>
        {isNow ? "NOW" : "NEXT"}
      </div>
      <div style={{ fontSize: "9px", letterSpacing: "0.2em", color: text, fontFamily: "Jost, sans-serif", marginBottom: "6px", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: "36px", color: text, fontWeight: 400, lineHeight: 1, fontFamily: "'Cormorant Garamond', serif" }}>{number}</div>
      {subtitle && (
        <div style={{ fontSize: "10px", color: text, marginTop: "4px", fontStyle: "italic", fontFamily: "Georgia, serif" }}>{subtitle}</div>
      )}
    </div>
  );
}

function ChakraCellV2({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: "9px", letterSpacing: "0.18em", color: "#6A0DAD", fontFamily: "Jost, sans-serif", marginBottom: "2px" }}>{label}</div>
      <div style={{ fontSize: "13px", color: "#1A1030", fontFamily: "Jost, sans-serif" }}>{value}</div>
    </div>
  );
}

function SoulEssenceCard({ firstName, body }) {
  const firstLetter = (firstName || "").charAt(0) || "";
  const rest = body || "";
  return (
    <div style={{ background: "linear-gradient(180deg, white 0%, #FAF8F4 100%)", border: "1px solid rgba(212,168,85,0.3)", borderRadius: "16px", padding: "28px 26px" }}>
      <p style={{ margin: 0, fontSize: "15px", lineHeight: 1.85, color: "#1A1030", fontFamily: "Georgia, 'Cormorant Garamond', serif" }}>
        <span style={{ float: "left", fontSize: "56px", lineHeight: 0.85, padding: "6px 10px 0 0", color: "#6A0DAD", fontWeight: 500, fontFamily: "'Cormorant Garamond', serif" }} aria-hidden>
          {firstLetter}
        </span>
        {rest}
      </p>
    </div>
  );
}

function ContentCard({ body }) {
  // The rewritten templates can mix prose with `- ` bulleted or `1.` numbered
  // list lines separated by \n. Parse into paragraph / list blocks so the
  // Sacred Report renders those as real HTML lists instead of one long <p>.
  // Any `**text**` markers become <strong> for lead-in emphasis.
  const blocks = parseContentBlocks(body || "");
  return (
    <div style={{ padding: "0 8px", marginBottom: "24px", maxWidth: "640px", marginLeft: "auto", marginRight: "auto" }}>
      {blocks.map((b, i) => {
        const key = `blk-${i}`;
        if (b.kind === "ol") {
          return (
            <ol key={key} style={{ margin: "0 0 14px", paddingLeft: "24px", fontSize: "14px", lineHeight: 1.85, color: "#1A1030", fontFamily: "Georgia, serif" }}>
              {b.items.map((it, j) => (<li key={j} style={{ marginBottom: "6px" }}>{renderInline(it)}</li>))}
            </ol>
          );
        }
        if (b.kind === "ul") {
          return (
            <ul key={key} style={{ margin: "0 0 14px", paddingLeft: "20px", fontSize: "14px", lineHeight: 1.85, color: "#1A1030", fontFamily: "Georgia, serif" }}>
              {b.items.map((it, j) => (<li key={j} style={{ marginBottom: "6px" }}>{renderInline(it)}</li>))}
            </ul>
          );
        }
        return (
          <p key={key} style={{ margin: "0 0 14px", fontSize: "14px", lineHeight: 1.85, color: "#1A1030", fontFamily: "Georgia, serif" }}>{renderInline(b.text)}</p>
        );
      })}
    </div>
  );
}

// Split a body string on blank lines into blocks. If every non-empty line in a
// block starts with a bullet marker (- , * , or 1. / 2. etc.), treat the
// block as a list; otherwise treat it as a paragraph.
function parseContentBlocks(body) {
  const chunks = String(body).split(/\n{2,}/).map((c) => c.trim()).filter(Boolean);
  return chunks.map((chunk) => {
    const lines = chunk.split(/\n/).map((l) => l.trim()).filter(Boolean);
    const olMatch = lines.every((l) => /^\d+[.)]\s+/.test(l));
    const ulMatch = lines.every((l) => /^[-*\u2022]\s+/.test(l));
    if (olMatch && lines.length >= 2) {
      return { kind: "ol", items: lines.map((l) => l.replace(/^\d+[.)]\s+/, "")) };
    }
    if (ulMatch && lines.length >= 2) {
      return { kind: "ul", items: lines.map((l) => l.replace(/^[-*\u2022]\s+/, "")) };
    }
    return { kind: "p", text: chunk.replace(/\n/g, " ") };
  });
}

// Render inline `**bold**` spans as <strong>. Everything else stays plain text.
function renderInline(str) {
  const s = String(str);
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(p);
    if (m) return <strong key={i}>{m[1]}</strong>;
    return <span key={i}>{p}</span>;
  });
}

function GuidanceCard({ variant, items }) {
  const isDo = variant === "do";
  const bg = isDo ? "linear-gradient(180deg, #F0FDF4 0%, white 100%)" : "linear-gradient(180deg, #FEF2F2 0%, white 100%)";
  const border = isDo ? "#22C55E" : "#EF4444";
  const labelColor = isDo ? "#15803D" : "#991B1B";
  const label = isDo ? "SACRED GUIDANCE — DO" : "SACRED GUIDANCE — AVOID";
  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: "12px", padding: "18px" }}>
      <div style={{ fontSize: "11px", letterSpacing: "0.22em", color: labelColor, fontFamily: "Jost, sans-serif", marginBottom: "14px", fontWeight: 500 }}>{label}</div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {items.map((it) => (
          <li key={it} style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "10px" }}>
            <svg width="12" height="12" viewBox="0 0 12 12" style={{ flexShrink: 0, marginTop: "4px" }}>
              {isDo ? (
                <>
                  <circle cx="6" cy="6" r="5" fill="none" stroke="#22C55E" strokeWidth="1" />
                  <path d="M3.5,6 L5.5,8 L8.5,4" fill="none" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </>
              ) : (
                <>
                  <circle cx="6" cy="6" r="5" fill="none" stroke="#EF4444" strokeWidth="1" />
                  <path d="M4,4 L8,8 M8,4 L4,8" fill="none" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
                </>
              )}
            </svg>
            <span style={{ fontSize: "13px", lineHeight: 1.6, color: "#1A1030", fontFamily: "Georgia, serif" }}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
